import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { parseFloatFromString } from '../utils/formatters';
import Cookies from 'js-cookie';
import { toast } from 'react-hot-toast';
import { usePortfolioContext } from '../contexts/PortfolioContext';
import { useAuthContext } from '../contexts/AuthContext';
import PortfolioRequired from '../components/PortfolioRequired';
import { usePositionContext } from '../contexts/PositionContext';
import { 
  calculateTotalAllocation, 
  createAllocationData, 
  getRandomColor,
  getMarketCapInfo
} from '../utils/allocationHelpers';

// GraphQL queries
import { GET_ALL_TOKENS } from '../graphql/queries/tokens';
import { GET_ALL_ALLOCATION_PLANS, GET_ALLOCATION_PLAN } from '../graphql/queries/allocationPlans';
import { GET_TOKEN_LOCATIONS } from '../graphql/queries/tokenLocations';
import { CREATE_TOKEN_LOCATION, RENAME_TOKEN_LOCATION, DELETE_TOKEN_LOCATION } from '../graphql/mutations/tokenLocations';

// Components
import AllocationTable from '../components/allocation/AllocationTable';
import { PlannerHeader } from '../components/allocation/PlannerPresentation';
import CategoryBreakdown from '../components/allocation/CategoryBreakdown';
import AllocationPlanManagementModal from '../components/allocation/AllocationPlanManagementModal';
import AddTokenModal from '../components/allocation/AddTokenModal';
import TokenAssignmentModal from '../components/TokenAssignmentModal';
import AllocationQuickBuyModal from '../components/allocation/AllocationQuickBuyModal';
import AllocationLocationSelector from '../components/allocation/AllocationLocationSelector';
import EmptyState from '../components/EmptyState';


// GraphQL mutations
import { UPDATE_ALLOCATION_PLAN } from '../graphql/mutations/allocationPlans';

const AllocationPlannerPage = () => {
  const { currentPortfolioId } = usePortfolioContext();
  const { user } = useAuthContext();
  const { loading: loadingTokens, error: errorTokens, data: tokensData, refetch: refetchTokens } = useQuery(GET_ALL_TOKENS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId
  });
  const { positions, loading: loadingPositions, error: errorPositions } = usePositionContext();
  const { data: locationsData, refetch: refetchLocations } = useQuery(GET_TOKEN_LOCATIONS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId
  });
  
  // Cookie key helpers (namespaced by portfolio)
  const getSelectedPlanCookieKey = useCallback((portfolioId) => (
    portfolioId ? `selectedPlanId_${portfolioId}` : 'selectedPlanId'
  ), []);
  const getAllocationPlanCookieKey = useCallback((portfolioId) => (
    portfolioId ? `allocationPlan_${portfolioId}` : 'allocationPlan'
  ), []);

  // For temporary allocations stored in cookies
  const [allocations, setAllocations] = useState(() => {
    if (!currentPortfolioId) {
      return [{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }];
    }
    // Only load from cookies if we have a selected plan for this portfolio
    const selectedPlanId = Cookies.get(getSelectedPlanCookieKey(currentPortfolioId));
    if (selectedPlanId) {
      const savedAllocationsJson = Cookies.get(getAllocationPlanCookieKey(currentPortfolioId));
      if (savedAllocationsJson) {
        try {
          // Load allocations from cookies, filter out empty rows, and add a new empty row
          const savedAllocations = JSON.parse(savedAllocationsJson);
          const validAllocations = savedAllocations
            .filter(allocation => allocation.tokenId && parseFloatFromString(allocation.amount) >= 0)
            .map(allocation => ({
              ...allocation,
              completed: allocation.completed || false
            }));
          
          // Add empty row at the end for new additions
          if (validAllocations.length > 0) {
            validAllocations.push({ tokenId: '', coingeckoApiId: '', amount: 100, completed: false });
            return validAllocations;
          }
        } catch (error) {
          console.error('Error parsing saved allocations:', error);
        }
      }
    }
    // Default to single empty row
    return [{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }];
  });

  // Plan management state
  const [planName, setPlanName] = useState('');
  const [savedPlans, setSavedPlans] = useState([]); 
  const [selectedPlanId, setSelectedPlanId] = useState(() => (
    currentPortfolioId ? (Cookies.get(getSelectedPlanCookieKey(currentPortfolioId)) || null) : null
  ));
  const [showManagePlansModal, setShowManagePlansModal] = useState(false);
  const [showAddTokenModal, setShowAddTokenModal] = useState(false);
  const [showEditTokenModal, setShowEditTokenModal] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editPreset, setEditPreset] = useState(null);
  const [assignmentModal, setAssignmentModal] = useState({ isOpen: false, type: 'group', token: null });
  const [targetLocationModal, setTargetLocationModal] = useState({ isOpen: false, index: null });
  const [completeModal, setCompleteModal] = useState({ isOpen: false, token: null, amount: 0 });
  
  // Token selector state
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null);
  
  // Debounce timer for auto-save on amount changes
  const amountChangeTimerRef = useRef(null);

  // Queries and mutations for database plans
  const { data: plansData, refetch: refetchPlans } = useQuery(GET_ALL_ALLOCATION_PLANS, {
    variables: { portfolioId: currentPortfolioId },
    skip: !currentPortfolioId,
    fetchPolicy: 'network-only'
  });

  // Add lazy query to load full plan details
  const [getPlanDetails, { data: loadedPlanData }] = useLazyQuery(GET_ALLOCATION_PLAN, {
    fetchPolicy: 'network-only'
  });

  // Setup mutations
  const [updateAllocationPlan] = useMutation(UPDATE_ALLOCATION_PLAN);
  const [createTokenLocation] = useMutation(CREATE_TOKEN_LOCATION);
  const [renameTokenLocation] = useMutation(RENAME_TOKEN_LOCATION);
  const [deleteTokenLocation] = useMutation(DELETE_TOKEN_LOCATION);

  // Refresh a single allocation's token info after external edits (group/location)
  const refreshAllocationTokenInfo = useCallback(async (tokenId, coingeckoApiId) => {
    try {
      const res = await refetchTokens();
      const tokensList = res?.data?.tokens || [];
      const updatedToken = tokensList.find(t => t.id === tokenId || (coingeckoApiId && t.coingeckoApiId === coingeckoApiId));
      if (!updatedToken) return;
      setAllocations(prev => prev.map(a => {
        const matchesById = a?.token?.id === tokenId || a?.tokenId === tokenId;
        const matchesByCg = coingeckoApiId && (a?.token?.coingeckoApiId === coingeckoApiId || a?.coingeckoApiId === coingeckoApiId);
        if (matchesById || matchesByCg) {
          return {
            ...a,
            token: {
              ...(a.token || {}),
              ...updatedToken
            }
          };
        }
        return a;
      }));
    } catch (_) {}
  }, [refetchTokens]);

  // Refresh a single allocation's token info after external edits (group/location)
  // duplicate removed

  // Helper function to generate a plan name
  const generatePlanName = useCallback(() => {
    const now = new Date();
    const month = now.toLocaleString('en-US', { month: 'long' });
    const day = now.getDate();
    return `Allocation plan ${month} ${day}`;
  }, []);

  // Helper function to transform component data to GraphQL format
  const transformToGraphQLFormat = useCallback((allocations) => {
    return allocations
      .filter(a => (a.tokenId || a.coingeckoApiId) && parseFloatFromString(a.amount) >= 0)
      .map(a => {
        const amount = parseFloatFromString(a.amount);
        // Ensure amount is valid and non-negative
        if (amount < 0 || isNaN(amount)) {
          console.warn('Invalid amount detected:', a.amount, 'parsed as:', amount);
          return null;
        }
        return {
          tokenId: a.tokenId || null,
          coingeckoApiId: a.coingeckoApiId || null,
          amount: amount.toString(),
          completed: a.completed || false,
          targetLocationId: a.targetLocation?.id || a.targetLocationId || null
        };
      })
      .filter(Boolean); // Remove any null entries
  }, []);

  // Auto-save helper function
  const autoSave = useCallback(async (allocationsToSave) => {
    if (!selectedPlanId) {
      return;
    }

    try {
      // Only save complete rows with token and non-negative value
      const validAllocations = allocationsToSave.filter(a => 
        (a.tokenId || a.coingeckoApiId) && parseFloatFromString(a.amount) >= 0
      );
      
      const planNameToUse = planName || generatePlanName();
      const mappedAllocations = transformToGraphQLFormat(validAllocations);
      
      await updateAllocationPlan({
        variables: { 
          planId: selectedPlanId,
          name: planNameToUse,
          allocations: mappedAllocations
        }
      });
      
      // Silently save without toast to avoid spamming user
      refetchPlans();
    } catch (error) {
      console.error('Error auto-saving plan:', error);
      toast.error('Failed to save changes');
    }
  }, [selectedPlanId, planName, updateAllocationPlan, refetchPlans, generatePlanName, transformToGraphQLFormat]);

  // Transform GraphQL data to component format
  const transformAllocationData = useCallback((allocations) => {
    if (!allocations || allocations.length === 0) {
      return [{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }];
    }

    const validAllocations = allocations
      .filter(allocation => allocation.token?.id && parseFloatFromString(allocation.amount) >= 0)
      .map(allocation => ({
        tokenId: allocation.token.id,
        coingeckoApiId: allocation.token.coingeckoApiId || '',
        amount: parseFloatFromString(allocation.amount),
        completed: allocation.completed || false,
        targetLocation: allocation.targetLocation || null,
        targetLocationId: allocation.targetLocation?.id || '',
        token: allocation.token
      }));

    if (validAllocations.length === 0) {
      validAllocations.push({ tokenId: '', coingeckoApiId: '', amount: 100, completed: false });
    }

    return validAllocations;
  }, []);


  // Function to load a specific plan by ID
  const loadPlanById = useCallback((planId) => {
    if (planId) {
      // Only load plan details if the plan exists in the current portfolio's plans list
      if (!savedPlans.some(p => p.id === planId)) {
        return;
      }
      getPlanDetails({ variables: { id: planId } });
    } else {
      // Clear allocations if no plan is selected
      setAllocations([{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }]);
      setPlanName('');
    }
  }, [getPlanDetails, savedPlans]);

  // Handle the actual data loading once the query completes is now in an effect below

  // Use Effect Hooks
  
  // Reset selection whenever switching portfolios or when no portfolio is selected
  useEffect(() => {
    // Clear legacy global cookies
    Cookies.remove('selectedPlanId');
    Cookies.remove('allocationPlan');

    if (!currentPortfolioId) {
      setSelectedPlanId(null);
      setPlanName('');
      setAllocations([{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }]);
      return;
    }

    // When portfolio changes, ensure selected plan comes from its namespaced cookie if present
    const cookiePlanId = Cookies.get(getSelectedPlanCookieKey(currentPortfolioId)) || null;
    setSelectedPlanId(cookiePlanId);
    if (!cookiePlanId) {
      setPlanName('');
      setAllocations([{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }]);
    }
  }, [currentPortfolioId, getSelectedPlanCookieKey]);

  // Clear all allocation cookies on auth user change to prevent cross-account leakage
  useEffect(() => {
    const all = Cookies.get();
    Object.keys(all || {}).forEach((key) => {
      if (key === 'selectedPlanId' || key === 'allocationPlan' || key.startsWith('selectedPlanId_') || key.startsWith('allocationPlan_')) {
        Cookies.remove(key);
      }
    });
  }, [user?.id]);

  // Load selected plan only if it belongs to the current portfolio's saved plans
  useEffect(() => {
    if (selectedPlanId && savedPlans.some(p => p.id === selectedPlanId)) {
      loadPlanById(selectedPlanId);
    }
  }, [selectedPlanId, loadPlanById, savedPlans]);

  // Use effect to track allocation changes and save to cookies (namespaced)
  useEffect(() => {
    // Filter out empty allocations before saving to cookies
    const validAllocations = allocations.filter(a => 
      (a.tokenId || a.coingeckoApiId) && parseFloatFromString(a.amount) >= 0
    );
    
    if (currentPortfolioId) {
      Cookies.set(getAllocationPlanCookieKey(currentPortfolioId), JSON.stringify(validAllocations), { expires: 7 });
    }
    
    // Save selected plan ID to cookies
    if (currentPortfolioId) {
      const key = getSelectedPlanCookieKey(currentPortfolioId);
      if (selectedPlanId) {
        Cookies.set(key, selectedPlanId, { expires: 7 });
      } else {
        Cookies.remove(key);
      }
    }
  }, [allocations, selectedPlanId, currentPortfolioId, getAllocationPlanCookieKey, getSelectedPlanCookieKey]);

  // Use effect to load plan data when it becomes available
  useEffect(() => {
    if (loadedPlanData?.allocationPlan) {
      const planData = loadedPlanData.allocationPlan;
      setPlanName(planData.name || '');
      const transformedAllocations = transformAllocationData(planData.allocations);
      setAllocations(transformedAllocations);
      setSelectedPlanId(planData.id);
    }
  }, [loadedPlanData, transformAllocationData]);

  // Listen for breakdown refresh events after quick add tx
  useEffect(() => {
    const handler = () => {
      try {
        // Refetch tokens for updated market data/group/location if needed
        refetchTokens?.();
      } catch (_) {}
      try {
        // If a plan is selected, refetch plan details to rebuild allocations and breakdowns
        if (selectedPlanId) {
          loadPlanById(selectedPlanId);
        }
      } catch (_) {}
    };
    window.addEventListener('allocation:refresh-breakdowns', handler);
    return () => window.removeEventListener('allocation:refresh-breakdowns', handler);
  }, [refetchTokens, selectedPlanId, loadPlanById]);

  // Effect to load user's saved plans
  useEffect(() => {
    if (plansData?.allAllocationPlans) {
      setSavedPlans(plansData.allAllocationPlans);
      // If the current selectedPlanId is not in the new list, or if portfolio changed, select the most recent plan
      const plans = plansData.allAllocationPlans;
      if (plans.length > 0) {
        // Find the most recently updated plan
        const mostRecentPlan = plans.reduce((latest, plan) => {
          if (!latest) return plan;
          return new Date(plan.updatedAt || plan.createdAt) > new Date(latest.updatedAt || latest.createdAt) ? plan : latest;
        }, null);
        if (!selectedPlanId || !plans.some(p => p.id === selectedPlanId)) {
          setSelectedPlanId(mostRecentPlan.id);
          setPlanName(mostRecentPlan.name);
          loadPlanById(mostRecentPlan.id);
        }
      } else {
        // No plans: clear selection and allocations
        setSelectedPlanId(null);
        setPlanName('');
        setAllocations([{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }]);
      }
    }
  }, [plansData, loadPlanById, selectedPlanId]);

  // Note: Position refetching on portfolio change is now handled by PositionContext

  useEffect(() => {
    if (!targetLocationModal.isOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') {
        setTargetLocationModal({ isOpen: false, index: null });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [targetLocationModal.isOpen]);

  // Handling loading and error states
  const loading = loadingTokens || loadingPositions;
  const error = errorTokens || errorPositions;

  // Guard when no portfolio is selected
  if (!currentPortfolioId) {
    return <PortfolioRequired title="Allocation Planner" description="Please create or select a portfolio to plan allocations." />;
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 flex justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 rounded-full border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-12">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Error loading data: {error.message}
        </div>
      </div>
    );
  }

  // Get data from GraphQL queries
  const { tokens = [] } = tokensData || {};
  const tokenLocations = locationsData?.tokenLocations || [];

  // Calculate total allocation (raw sum, may include empty rows)
  const totalAllocation = calculateTotalAllocation(allocations);

  // Create allocation data with token details
  const allocationData = createAllocationData(allocations, tokens, totalAllocation);
  const hasAllocationItems = allocationData.some(a => a.token && (typeof a.amount === 'number') && a.amount >= 0);

  // Calculate combined position data (existing + new allocations)
  const combinedTokenData = [...positions];
  
  // Add new allocations or update existing ones
  allocationData.forEach(allocation => {
    if (!allocation || !allocation.token || allocation.amount === undefined) {
      return; // Skip invalid allocations
    }
    
    const tokenId = allocation.token.id;
    const coingeckoApiId = allocation.token.coingeckoApiId;
    
    // Find existing position by tokenId or coingeckoApiId
    const existingPositionIndex = combinedTokenData.findIndex(p => 
      (tokenId && p.token?.id === tokenId && !tokenId.startsWith('temp-')) ||
      (coingeckoApiId && p.token?.coingeckoApiId === coingeckoApiId)
    );
    
    if (existingPositionIndex >= 0) {
      // Token already in portfolio, update values
      const existingPosition = {...combinedTokenData[existingPositionIndex]};
      const currentValue = parseFloatFromString(existingPosition.holdingsValue);
      const currentCost = parseFloatFromString(existingPosition.investedCost || existingPosition.current.basisCost);
      const currentPnL = parseFloatFromString(existingPosition.current.pnl);
      const currentPnlPercentage = parseFloatFromString(existingPosition.current.roi);
      
      // New allocation amount
      const newAllocationAmount = parseFloatFromString(allocation.amount);
      
      // Calculate new cost and PnL
      const newCost = currentCost + newAllocationAmount;
      const newPnlValue = currentPnL;
      const newPnlPercentage = newCost > 0 ? (newPnlValue / newCost) * 100 : 0;
      
      combinedTokenData[existingPositionIndex] = {
        ...existingPosition,
        holdingsValue: (currentValue + newAllocationAmount).toString(),
        current: {
          basisCost: existingPosition.current.basisCost,
          proceeds: existingPosition.current.proceeds,
          pnl: newPnlValue.toString(),
          roi: newPnlPercentage.toString(),
        },
        investedCost: newCost.toString(),
        originalPnl: currentPnL,
        originalPnlPercentage: currentPnlPercentage,
        originalValue: currentValue,
        newAllocation: newAllocationAmount,
        isChanged: true
      };
    } else {
      // New token to add to portfolio
      const newAllocationAmount = parseFloatFromString(allocation.amount);
      combinedTokenData.push({
        token: allocation.token,
        holdingsValue: allocation.amount.toString(),
        current: {
          basisCost: '0',
          proceeds: '0',
          pnl: '0',
          roi: '0',
        },
        investedCost: allocation.amount.toString(),
        originalPnl: 0,
        originalPnlPercentage: 0,
        originalValue: 0,
        newAllocation: newAllocationAmount,
        isNew: true
      });
    }
  });

  // Calculate new total portfolio value
  // const newPortfolioValue = combinedTokenData.reduce((sum, pos) => 
  //   sum + parseFloatFromString(pos.holdingsValue || 0), 0);

  // Group allocations by group and location (planned only, used for location UI) — removed unused aggregates

  // Build Market Cap breakdown: current vs after (pending only included in after)
  const currentTotalHoldings = positions.reduce((sum, p) => sum + parseFloatFromString(p.holdingsValue || 0), 0);
  const pendingTotalAllocation = allocationData.filter(a => !a.completed).reduce((sum, a) => sum + (a.amount || 0), 0);

  const marketCapCategories = {};
  // Current amounts per MC category from positions
  positions.forEach(pos => {
    if (!pos?.token) return;
    const category = getMarketCapInfo(pos.token.marketData?.marketCap).category;
    if (!marketCapCategories[category]) {
      const info = getMarketCapInfo(pos.token.marketData?.marketCap);
      marketCapCategories[category] = {
        name: category,
        color: info.color,
        amount: 0, // pending
        completedAmount: 0,
        currentAmount: 0,
        currentPercentage: 0,
        afterPercentage: 0,
      };
    }
    marketCapCategories[category].currentAmount += parseFloatFromString(pos.holdingsValue || 0);
  });
  // Pending (non-completed) planned amounts per MC category
  allocationData.forEach(item => {
    if (!item || !item.token || typeof item.amount !== 'number') return;
    const category = getMarketCapInfo(item.token.marketData?.marketCap).category;
    if (!marketCapCategories[category]) {
      const info = getMarketCapInfo(item.token.marketData?.marketCap);
      marketCapCategories[category] = {
        name: category,
        color: info.color,
        amount: 0,
        completedAmount: 0,
        currentAmount: 0,
        currentPercentage: 0,
        afterPercentage: 0,
      };
    }
    if (item.completed) {
      marketCapCategories[category].completedAmount += item.amount;
    } else {
      marketCapCategories[category].amount += item.amount;
    }
  });
  // Compute current/after percentages
  const afterTotal = currentTotalHoldings + pendingTotalAllocation;
  Object.values(marketCapCategories).forEach(cat => {
    cat.currentPercentage = currentTotalHoldings > 0 ? (cat.currentAmount / currentTotalHoldings) * 100 : 0;
    const afterAmount = cat.currentAmount + cat.amount; // do not include completed
    cat.afterPercentage = afterTotal > 0 ? (afterAmount / afterTotal) * 100 : 0;
  });

  // Build Group breakdown: current vs after
  const groupChangeCategories = {};
  // Current by group from positions
  positions.forEach(pos => {
    const gid = pos?.token?.group?.id || 'unknown';
    const gname = pos?.token?.group?.name || 'Unknown';
    if (!groupChangeCategories[gid]) {
      groupChangeCategories[gid] = {
        id: gid,
        name: gname,
        amount: 0, // pending
        completedAmount: 0,
        currentAmount: 0,
        currentPercentage: 0,
        afterPercentage: 0,
      };
    }
    groupChangeCategories[gid].currentAmount += parseFloatFromString(pos.holdingsValue || 0);
  });
  // Pending/Completed by group from allocations
  allocationData.forEach(item => {
    if (!item || !item.token || typeof item.amount !== 'number') return;
    const gid = item.groupId || 'unknown';
    const gname = item.groupName || 'Unknown';
    if (!groupChangeCategories[gid]) {
      groupChangeCategories[gid] = {
        id: gid,
        name: gname,
        amount: 0,
        completedAmount: 0,
        currentAmount: 0,
        currentPercentage: 0,
        afterPercentage: 0,
      };
    }
    if (item.completed) {
      groupChangeCategories[gid].completedAmount += item.amount;
    } else {
      groupChangeCategories[gid].amount += item.amount;
    }
  });
  Object.values(groupChangeCategories).forEach(cat => {
    cat.currentPercentage = currentTotalHoldings > 0 ? (cat.currentAmount / currentTotalHoldings) * 100 : 0;
    const afterAmount = cat.currentAmount + cat.amount;
    cat.afterPercentage = afterTotal > 0 ? (afterAmount / afterTotal) * 100 : 0;
  });

  // Build Location breakdown: current vs after
  const locationChangeCategories = {};
  // Current by location from cached location balances. Fall back to the legacy
  // token-level location only when a position has no split balances yet.
  positions.forEach(pos => {
    const currentPrice = parseFloatFromString(pos?.token?.marketData?.currentPrice || 0);
    const balances = Array.isArray(pos?.locationBalances) ? pos.locationBalances : [];
    if (balances.length > 0) {
      balances.forEach(balance => {
        const lid = balance.locationId || 'unknown';
        const lname = balance.name || 'Unknown';
        if (!locationChangeCategories[lid]) {
          locationChangeCategories[lid] = {
            id: lid,
            name: lname,
            amount: 0, // pending
            completedAmount: 0,
            currentAmount: 0,
            currentPercentage: 0,
            afterPercentage: 0,
          };
        }
        locationChangeCategories[lid].currentAmount += parseFloatFromString(balance.quantity || 0) * currentPrice;
      });
      return;
    }

    const lid = pos?.token?.location?.id || 'unknown';
    const lname = pos?.token?.location?.name || 'Unknown';
    if (!locationChangeCategories[lid]) {
      locationChangeCategories[lid] = {
        id: lid,
        name: lname,
        amount: 0, // pending
        completedAmount: 0,
        currentAmount: 0,
        currentPercentage: 0,
        afterPercentage: 0,
      };
    }
    locationChangeCategories[lid].currentAmount += parseFloatFromString(pos.holdingsValue || 0);
  });
  // Pending/Completed by location from allocations
  allocationData.forEach(item => {
    if (!item || !item.token || typeof item.amount !== 'number') return;
    const lid = item.locationId || 'unknown';
    const lname = item.locationName || 'Unknown';
    if (!locationChangeCategories[lid]) {
      locationChangeCategories[lid] = {
        id: lid,
        name: lname,
        amount: 0,
        completedAmount: 0,
        currentAmount: 0,
        currentPercentage: 0,
        afterPercentage: 0,
      };
    }
    if (item.completed) {
      locationChangeCategories[lid].completedAmount += item.amount;
    } else {
      locationChangeCategories[lid].amount += item.amount;
    }
  });
  Object.values(locationChangeCategories).forEach(cat => {
    cat.currentPercentage = currentTotalHoldings > 0 ? (cat.currentAmount / currentTotalHoldings) * 100 : 0;
    const afterAmount = cat.currentAmount + cat.amount;
    cat.afterPercentage = afterTotal > 0 ? (afterAmount / afterTotal) * 100 : 0;
  });

  // Calculate portfolio value changes
  // const portfolioValueChange = newPortfolioValue - currentPortfolioValue;
  // const portfolioValueChangePercentage = currentPortfolioValue > 0 
  //   ? (portfolioValueChange / currentPortfolioValue) * 100 
  //   : 100;


  // Event Handlers
  const handleAddRow = () => {
    const newAllocations = [...allocations, { tokenId: '', coingeckoApiId: '', amount: 100, completed: false }];
    setAllocations(newAllocations);
    // Don't auto-save when adding an empty row
  };

  const handleAddCompleteRow = async (tokenSelection, amount, targetLocationId = '') => {
    const { tokenId, coingeckoApiId, coingeckoSlug, name, ticker } = tokenSelection;
    
    // Check if this token is already in the allocations list
    const tokenAlreadyExists = allocations.some((allocation) => 
      (tokenId && allocation.tokenId === tokenId) ||
      (coingeckoApiId && allocation.coingeckoApiId === coingeckoApiId)
    );
    
    if (tokenAlreadyExists) {
      toast.error('This token is already in your allocation list');
      return;
    }
    
    // Create a complete allocation object
    const newAllocation = {
      tokenId: tokenId || '',
      coingeckoApiId: coingeckoApiId || '',
      amount: amount ?? 100,
      completed: false,
      token: {
        id: tokenId || `temp-${coingeckoApiId}`,
        name: name || 'Selected Token',
        ticker: ticker || 'N/A',
        coingeckoApiId: coingeckoApiId,
        coingeckoSlug: coingeckoSlug || null,
        group: null,
        location: null,
        marketData: null
      },
      targetLocation: tokenLocations.find(l => l.id === targetLocationId) || null,
      targetLocationId: targetLocationId || '',
      selectedTokenInfo: { name, ticker }
    };
    
    const newAllocations = [...allocations, newAllocation];
    setAllocations(newAllocations);
    
    // Auto-save the changes
    await autoSave(newAllocations);
  };

  const handleConfirmAddToken = async (tokenSelection, amount, _planId, targetLocationId) => {
    await handleAddCompleteRow(tokenSelection, amount, targetLocationId);
    setShowAddTokenModal(false);
  };

  const handleRemoveRow = async (index) => {
    // Check if we're removing the only row
    if (allocations.length === 1) {
      // If it's the last row, just clear it rather than removing it
      const emptyAllocations = [{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }];
      setAllocations(emptyAllocations);
      // Auto-save (will save empty list)
      await autoSave(emptyAllocations);
      return;
    }
    
    // Remove the row
    const newAllocations = allocations.filter((_, i) => i !== index);
    
    // Check if we've removed all the rows with values
    const validAllocations = newAllocations.filter(
      a => (a.tokenId || a.coingeckoApiId) && parseFloatFromString(a.amount) >= 0
    );
    
    // If no valid allocations left, make sure we have an empty row
    if (validAllocations.length === 0 && !newAllocations.some(a => !a.tokenId)) {
      newAllocations.push({ tokenId: '', coingeckoApiId: '', amount: 100, completed: false });
    }
    
    // Update state
    setAllocations(newAllocations);
    
    // Auto-save the changes
    await autoSave(newAllocations);
  };

  const handleUpdateAmount = (index, amount, immediate = false) => {
    // Prevent negative amounts
    if (parseFloatFromString(amount) < 0) {
      toast.error('Negative allocation amounts are not allowed');
      return;
    }
    
    const newAllocations = [...allocations];
    newAllocations[index].amount = amount;
    setAllocations(newAllocations);
    
    // Debounce auto-save to avoid saving on every keystroke
    if (amountChangeTimerRef.current) {
      clearTimeout(amountChangeTimerRef.current);
    }
    
    // Auto-save if amount is valid (>= 0) and token is selected
    const allocation = newAllocations[index];
    if ((allocation.tokenId || allocation.coingeckoApiId) && parseFloatFromString(amount) >= 0) {
      if (immediate) {
        autoSave(newAllocations);
      } else {
        amountChangeTimerRef.current = setTimeout(() => {
          autoSave(newAllocations);
        }, 1000); // Wait 1 second after user stops typing
      }
    }
  };

  const handleSelectToken = (tokenSelection, index) => {
    const { tokenId, coingeckoApiId, coingeckoSlug, name, ticker } = tokenSelection;
    
    // Check if this token is already in the allocations list
    const tokenAlreadyExists = allocations.some((allocation, i) => 
      i !== index && (
        (tokenId && allocation.tokenId === tokenId) ||
        (coingeckoApiId && allocation.coingeckoApiId === coingeckoApiId)
      )
    );
    
    if (tokenAlreadyExists) {
      toast.error('This token is already in your allocation list');
      return;
    }
    
    const newAllocations = [...allocations];
    
    // Make sure the index exists in the array
    if (!newAllocations[index]) {
      console.error('Allocation index does not exist:', index);
      return;
    }
    
    newAllocations[index].tokenId = tokenId || '';
    newAllocations[index].coingeckoApiId = coingeckoApiId || '';
    
    // Create a proper token object for immediate display
    newAllocations[index].token = {
      id: tokenId || `temp-${coingeckoApiId}`,
      name: name || 'Selected Token',
      ticker: ticker || 'N/A',
      coingeckoApiId: coingeckoApiId,
      coingeckoSlug: coingeckoSlug || null, // Only available for imported tokens
      group: null,
      location: null,
      marketData: null
    };
    
    // Store token info for backward compatibility
    newAllocations[index].selectedTokenInfo = { name, ticker };
    
    setAllocations(newAllocations);
    setActiveDropdownIndex(null);
  };

  const handleReorderRows = async (fromIndex, toIndex) => {
    const newAllocations = [...allocations];
    const [movedItem] = newAllocations.splice(fromIndex, 1);
    newAllocations.splice(toIndex, 0, movedItem);
    setAllocations(newAllocations);
    
    // Auto-save the changes
    await autoSave(newAllocations);
  };

  const handleUpdateCompleted = async (index, completed) => {
    const newAllocations = [...allocations];
    newAllocations[index].completed = completed;
    setAllocations(newAllocations);
    
    // Auto-save the changes
    await autoSave(newAllocations);

    // If just completed, open transaction suggestion modal
    try {
      if (completed) {
        const rawToken = newAllocations[index]?.token;
        // Prefer fully-resolved token from tokens query (scaled to portfolio currency)
        let token = rawToken;
        if (rawToken) {
          const byId = tokens.find(t => t.id === rawToken.id);
          const byCg = !byId && rawToken.coingeckoApiId ? tokens.find(t => t.coingeckoApiId === rawToken.coingeckoApiId) : null;
          token = byId || byCg || rawToken;
        }
        const amountValue = parseFloatFromString(newAllocations[index]?.amount) || 0;
        if (token && amountValue > 0) {
          setCompleteModal({
            isOpen: true,
            token,
            amount: amountValue,
            targetLocation: newAllocations[index]?.targetLocation || null
          });
        }
      }
    } catch (_) {}
  };

  const handleLoadPlan = (planId) => {
    try {
      if (!planId) {
        // Clear the current plan
        setSelectedPlanId(null);
        setPlanName('');
        setAllocations([{ tokenId: '', coingeckoApiId: '', amount: 100, completed: false }]);
        return;
      }
      
      // Set selected plan ID first, then load plan data
      setSelectedPlanId(planId);
      loadPlanById(planId);
    } catch (error) {
      console.error('Error loading plan:', error);
      toast.error('Failed to load plan');
    }
  };

  const handleEditRow = (index) => {
    const item = allocations[index];
    if (!item) return;
    setEditIndex(index);
    setEditPreset({
      tokenSelection: {
        tokenId: item.token?.id || item.tokenId || '',
        coingeckoApiId: item.token?.coingeckoApiId || item.coingeckoApiId || '',
        name: item.token?.name || '',
        ticker: item.token?.ticker || ''
      },
      amount: parseFloatFromString(item.amount),
      targetLocationId: item.targetLocation?.id || item.targetLocationId || ''
    });
    setShowEditTokenModal(true);
  };

  const handleConfirmEditToken = async (tokenSelection, amount, _planId, targetLocationId) => {
    if (editIndex === null) return;
    // Apply changes to allocations array
    const newAllocations = [...allocations];
    newAllocations[editIndex] = {
      ...newAllocations[editIndex],
      tokenId: tokenSelection.tokenId || '',
      coingeckoApiId: tokenSelection.coingeckoApiId || '',
      token: {
        id: tokenSelection.tokenId || `temp-${tokenSelection.coingeckoApiId}`,
        name: tokenSelection.name,
        ticker: tokenSelection.ticker,
        coingeckoApiId: tokenSelection.coingeckoApiId,
        coingeckoSlug: null,
        group: null,
        location: null,
        marketData: null
      },
      targetLocation: tokenLocations.find(l => l.id === targetLocationId) || null,
      targetLocationId: targetLocationId || '',
      amount: amount
    };
    setAllocations(newAllocations);
    setShowEditTokenModal(false);
    setEditIndex(null);
    setEditPreset(null);
    await autoSave(newAllocations);
  };


  const handleCreateTargetLocation = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed || !currentPortfolioId) return null;
    try {
      await createTokenLocation({ variables: { portfolioId: currentPortfolioId, name: trimmed } });
      const result = await refetchLocations?.();
      const updatedLocations = result?.data?.tokenLocations || [];
      const created = updatedLocations.find(l => l.name.toLowerCase() === trimmed.toLowerCase());
      toast.success(created ? `Created location ${created.name}` : 'Created location');
      return created || null;
    } catch (error) {
      console.error('Error creating location:', error);
      toast.error('Failed to create location');
      return null;
    }
  };

  const handleRenameTargetLocation = async (locationId, name) => {
    const trimmed = (name || '').trim();
    if (!locationId || !trimmed || !currentPortfolioId) return false;
    try {
      await renameTokenLocation({ variables: { portfolioId: currentPortfolioId, locationId, name: trimmed } });
      await refetchLocations?.();
      setAllocations(current => current.map(allocation => {
        const allocationLocationId = allocation.targetLocation?.id || allocation.targetLocationId;
        if (allocationLocationId !== locationId) return allocation;
        return {
          ...allocation,
          targetLocation: allocation.targetLocation ? { ...allocation.targetLocation, name: trimmed } : { id: locationId, name: trimmed },
        };
      }));
      toast.success('Location renamed');
      return true;
    } catch (error) {
      console.error('Error renaming location:', error);
      toast.error('Failed to rename location');
      return false;
    }
  };

  const handleDeleteTargetLocation = async (location) => {
    if (!location?.id || !currentPortfolioId) return false;
    try {
      const result = await deleteTokenLocation({ variables: { portfolioId: currentPortfolioId, locationId: location.id } });
      if (result?.data?.deleteTokenLocation !== true) {
        throw new Error('Location was not deleted');
      }
      await refetchLocations?.();
      setAllocations(current => current.map(allocation => {
        const allocationLocationId = allocation.targetLocation?.id || allocation.targetLocationId;
        if (allocationLocationId !== location.id) return allocation;
        return {
          ...allocation,
          targetLocation: null,
          targetLocationId: '',
        };
      }));
      toast.success(`Deleted location ${location.name || ''}`.trim());
      return true;
    } catch (error) {
      console.error('Error deleting location:', error);
      const lower = String(error?.message || '').toLowerCase();
      const message = lower.includes('assigned') || lower.includes('transaction') || lower.includes('balance') || lower.includes('internal error')
        ? 'Cannot delete location: it is used by transactions or balances.'
        : 'Failed to delete location';
      toast.error(message);
      return false;
    }
  };

  const openAssignment = (type, index) => {
    const item = allocations[index];
    if (!item || !item.token) return;
    setAssignmentModal({ isOpen: true, type, token: item.token });
  };
  const openTargetLocation = (index) => {
    if (!allocations[index]) return;
    setTargetLocationModal({ isOpen: true, index });
  };
  const closeTargetLocation = () => {
    setTargetLocationModal({ isOpen: false, index: null });
  };
  const applyTargetLocation = async (locationId, selectedLocation = null) => {
    const idx = targetLocationModal.index;
    if (idx === null || idx === undefined) return;
    const selected = selectedLocation || (locationsData?.tokenLocations || []).find(l => l.id === locationId) || null;
    const newAllocations = [...allocations];
    newAllocations[idx] = {
      ...newAllocations[idx],
      targetLocation: selected,
      targetLocationId: selected?.id || ''
    };
    setAllocations(newAllocations);
    closeTargetLocation();
    await autoSave(newAllocations);
  };


  const closeAssignment = () => setAssignmentModal(m => ({ ...m, isOpen: false }));

  return (
    <div className="px-2 sm:px-3 lg:px-4 py-2">
      {/* Page Header */}
      <PlannerHeader title="Portfolio Allocation Planner" subtitle={selectedPlanId ? planName : null} onManagePlans={() => setShowManagePlansModal(true)} />
      
      {/* Allocation Plan Management Modal */}
      <AllocationPlanManagementModal
        isOpen={showManagePlansModal}
        onClose={() => setShowManagePlansModal(false)}
        currentPlanId={selectedPlanId}
        onSelectPlan={handleLoadPlan}
      />

      {/* Add Token Modal */}
      <AddTokenModal
        isOpen={showAddTokenModal}
        onClose={() => setShowAddTokenModal(false)}
        onConfirm={handleConfirmAddToken}
        allocations={allocations}
        locations={tokenLocations}
        onCreateTargetLocation={handleCreateTargetLocation}
        onRenameTargetLocation={handleRenameTargetLocation}
        onDeleteTargetLocation={handleDeleteTargetLocation}
      />

      {/* Edit Token Modal */}
      <AddTokenModal
        isOpen={showEditTokenModal}
        onClose={() => setShowEditTokenModal(false)}
        onConfirm={handleConfirmEditToken}
        allocations={allocations}
        isEdit={true}
        presetTokenSelection={editPreset?.tokenSelection}
        presetAmount={editPreset?.amount}
        allowedTokenSelection={editPreset?.tokenSelection}
        locations={tokenLocations}
        presetTargetLocationId={editPreset?.targetLocationId || ''}
        onCreateTargetLocation={handleCreateTargetLocation}
        onRenameTargetLocation={handleRenameTargetLocation}
        onDeleteTargetLocation={handleDeleteTargetLocation}
      />

      {/* Assignment Modal */}
      {assignmentModal.isOpen && assignmentModal.token && (
        <TokenAssignmentModal
          isOpen={assignmentModal.isOpen}
          onClose={closeAssignment}
          type={assignmentModal.type}
          tokenId={assignmentModal.token.id}
          tokenTicker={assignmentModal.token.ticker}
          tokenCoingeckoApiId={assignmentModal.token.coingeckoApiId}
          currentItem={assignmentModal.token.group}
          onApplied={() => {
            // Refresh updated token info to reflect new group/location in the table buttons
            refreshAllocationTokenInfo(assignmentModal.token.id, assignmentModal.token.coingeckoApiId);
          }}
        />
      )}

      {/* Allocation target location modal */}
      {targetLocationModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={closeTargetLocation} />
          <div className="relative w-full max-w-2xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-lg shadow-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Set target location</h3>
              <button onClick={closeTargetLocation} className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200" aria-label="Close">
                <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
              </button>
            </div>
            <div className="px-6 py-4 flex-1 min-h-0 overflow-y-auto">
              <AllocationLocationSelector
                locations={tokenLocations}
                selectedLocationId={allocations[targetLocationModal.index]?.targetLocation?.id || allocations[targetLocationModal.index]?.targetLocationId || ''}
                onSelect={applyTargetLocation}
                onCreate={handleCreateTargetLocation}
                onRename={handleRenameTargetLocation}
                onDelete={handleDeleteTargetLocation}
                autoFocus
              />
            </div>
          </div>
        </div>
      )}

      {/* Complete -> Quick buy Modal */}
      {completeModal.isOpen && completeModal.token && (
        <AllocationQuickBuyModal
          isOpen={completeModal.isOpen}
          onClose={() => setCompleteModal({ isOpen: false, token: null, amount: 0 })}
          token={completeModal.token}
          amountValue={completeModal.amount}
          targetLocation={completeModal.targetLocation}
          locations={tokenLocations}
          onCreateTargetLocation={handleCreateTargetLocation}
          onRenameTargetLocation={handleRenameTargetLocation}
          onDeleteTargetLocation={handleDeleteTargetLocation}
        />
      )}

      {/* Allocation Table or Empty State */}
      {selectedPlanId && (
        hasAllocationItems ? (
          <AllocationTable 
            allocations={allocations}
            totalAllocation={totalAllocation}
            activeDropdownIndex={activeDropdownIndex}
            setActiveDropdownIndex={setActiveDropdownIndex}
            onAddRow={handleAddRow}
            onAddCompleteRow={handleAddCompleteRow}
            onRemoveRow={handleRemoveRow}
            onUpdateAmount={handleUpdateAmount}
            onSelectToken={handleSelectToken}
            onUpdateCompleted={handleUpdateCompleted}
            onEditRow={handleEditRow}
            onEditGroup={(i) => openAssignment('group', i)}
            onEditLocation={openTargetLocation}
            onReorderRows={handleReorderRows}
            onAddToken={() => setShowAddTokenModal(true)}
          />
        ) : (
          <EmptyState
            title="No allocations yet"
            description="Add tokens you consider buying to plan your allocation."
            secondaryAction={() => setShowAddTokenModal(true)}
            secondaryLabel="+ Add token"
            className="mb-8"
          />
        )
      )}
      {/* Show message if no plans exist */}
      {!selectedPlanId && savedPlans.length === 0 && (
        <EmptyState
          title="No allocation plans found"
          description="Create a new plan to get started!"
          action={() => setShowManagePlansModal(true)}
          actionLabel="Create Plan"
        />
      )}
      
      {/* Token Breakdown removed; columns merged into AllocationTable */}
      
      {allocationData.some(a => (typeof a.amount === 'number') && a.amount > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div>
            <CategoryBreakdown 
              title="Market Cap" 
              categories={Object.values(marketCapCategories).reduce((acc, cat) => {
                const totalAllocated = (cat.amount || 0) + (cat.completedAmount || 0);
                if (totalAllocated > 0) {
                  acc[cat.name] = {
                    id: cat.name,
                    name: cat.name,
                    amount: cat.amount,
                    completedAmount: cat.completedAmount,
                    currentPercentage: cat.currentPercentage,
                    afterPercentage: cat.afterPercentage,
                    color: cat.color
                  };
                }
                return acc;
              }, {})}
              getRandomColor={getRandomColor} 
            />
          </div>
          <div>
            <CategoryBreakdown 
              title="Group" 
              categories={Object.values(groupChangeCategories).reduce((acc, cat) => {
                const totalAllocated = (cat.amount || 0) + (cat.completedAmount || 0);
                if (totalAllocated > 0) {
                  acc[cat.id] = {
                    id: cat.id,
                    name: cat.name,
                    amount: cat.amount,
                    completedAmount: cat.completedAmount,
                    currentPercentage: cat.currentPercentage,
                    afterPercentage: cat.afterPercentage
                  };
                }
                return acc;
              }, {})}
              getRandomColor={getRandomColor} 
            />
          </div>
          <div>
            <CategoryBreakdown 
              title="Location" 
              categories={Object.values(locationChangeCategories).reduce((acc, cat) => {
                const totalAllocated = (cat.amount || 0) + (cat.completedAmount || 0);
                if (totalAllocated > 0) {
                  acc[cat.id] = {
                    id: cat.id,
                    name: cat.name,
                    amount: cat.amount,
                    completedAmount: cat.completedAmount,
                    currentPercentage: cat.currentPercentage,
                    afterPercentage: cat.afterPercentage
                  };
                }
                return acc;
              }, {})}
              getRandomColor={getRandomColor} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AllocationPlannerPage; 